import { MAIN_IDB_STORE } from '../util/browser/idb';

import { isCentralDbEnabled } from './centralDb/config';
import { pullAccessControlFromCentral, pushAccessControlToCentral, pushActivityToCentral } from './centralDb/sync';
import { RASITECH_SUPER_ADMIN_USER_ID } from './constants';

const STORE_KEY = 'rasi-access-control-v1';
const LOCAL_MIRROR_KEY = 'rasiTechAccessControl';

export type RasiActivityType =
  | 'login'
  | 'blocked_attempt'
  | 'feature_denied'
  | 'suspicious'
  | 'admin_action';

export type RasiActivityEntry = {
  id: string;
  userId: string;
  type: RasiActivityType;
  message: string;
  timestamp: number;
};

export type RasiAccessControlData = {
  paidUserIds: string[];
  blockedUserIds: string[];
  activityLog: RasiActivityEntry[];
};

const MAX_LOG = 500;

const EMPTY: RasiAccessControlData = {
  paidUserIds: [],
  blockedUserIds: [],
  activityLog: [],
};

let memory: RasiAccessControlData = { ...EMPTY, paidUserIds: [], blockedUserIds: [], activityLog: [] };
let loadPromise: Promise<void> | undefined;
let suppressCentralPush = false;
let lastCentralPullOk = true;
let lastCentralPushOk = true;

export function getCentralAccessSyncStatus() {
  return {
    enabled: isCentralDbEnabled(),
    pullOk: lastCentralPullOk,
    pushOk: lastCentralPushOk,
  };
}

const accessListeners = new Set<() => void>();

export function subscribeAccessControl(listener: () => void): () => void {
  accessListeners.add(listener);
  return () => accessListeners.delete(listener);
}

function notifyAccessControlChanged() {
  accessListeners.forEach((fn) => fn());
}

export function invalidateAccessControlCache() {
  loadPromise = undefined;
}

export async function reloadAccessControl(): Promise<RasiAccessControlData> {
  invalidateAccessControlCache();
  const data = await loadAccessControl();
  notifyAccessControlChanged();
  return data;
}

function mirrorToLocalStorage() {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(LOCAL_MIRROR_KEY, JSON.stringify(memory));
  } catch {
    // ignore quota
  }
}

function readLocalMirror(): RasiAccessControlData | undefined {
  if (typeof localStorage === 'undefined') return undefined;
  try {
    const raw = localStorage.getItem(LOCAL_MIRROR_KEY);
    if (!raw) return undefined;
    return JSON.parse(raw) as RasiAccessControlData;
  } catch {
    return undefined;
  }
}

export function getAccessControlData(): RasiAccessControlData {
  return memory;
}

export async function loadAccessControl(): Promise<RasiAccessControlData> {
  if (!loadPromise) {
    loadPromise = (async () => {
      let local = EMPTY;
      try {
        const stored = await MAIN_IDB_STORE.get<RasiAccessControlData>(STORE_KEY);
        local = normalize(stored || readLocalMirror() || EMPTY);
      } catch {
        local = normalize(readLocalMirror() || EMPTY);
      }

      if (isCentralDbEnabled()) {
        const remote = await pullAccessControlFromCentral();
        if (remote) {
          lastCentralPullOk = true;
          suppressCentralPush = true;
          memory = normalize({
            paidUserIds: remote.paidUserIds,
            blockedUserIds: remote.blockedUserIds,
            activityLog: remote.activityLog?.length
              ? remote.activityLog
              : local.activityLog,
          });
          suppressCentralPush = false;
        } else {
          lastCentralPullOk = false;
          memory = local;
        }
      } else {
        lastCentralPullOk = true;
        memory = local;
      }

      mirrorToLocalStorage();
      try {
        await MAIN_IDB_STORE.set(STORE_KEY, memory);
      } catch {
        // ignore
      }
    })();
  }
  await loadPromise;
  return memory;
}

export async function applyCentralAccessControl(data: RasiAccessControlData): Promise<void> {
  suppressCentralPush = true;
  memory = normalize(data);
  suppressCentralPush = false;
  mirrorToLocalStorage();
  try {
    await MAIN_IDB_STORE.set(STORE_KEY, memory);
  } catch {
    // ignore
  }
}

function normalizeUserId(id: string): string {
  return String(id).trim();
}

function normalize(data: RasiAccessControlData): RasiAccessControlData {
  return {
    paidUserIds: [...new Set((data.paidUserIds || []).map(normalizeUserId).filter(Boolean))],
    blockedUserIds: [...new Set((data.blockedUserIds || []).map(normalizeUserId).filter(Boolean))],
    activityLog: (data.activityLog || []).slice(-MAX_LOG),
  };
}

async function persist(actorId?: string): Promise<boolean> {
  memory = normalize(memory);
  mirrorToLocalStorage();
  try {
    await MAIN_IDB_STORE.set(STORE_KEY, memory);
  } catch {
    // IDB may be unavailable
  }
  let pushed = true;
  if (!suppressCentralPush && isCentralDbEnabled()) {
    pushed = await pushAccessControlToCentral(memory, actorId);
    lastCentralPushOk = pushed;
  }
  notifyAccessControlChanged();
  return pushed;
}

export function isPaidUserId(userId: string): boolean {
  const id = normalizeUserId(userId);
  if (id === RASITECH_SUPER_ADMIN_USER_ID) return true;
  return memory.paidUserIds.includes(id);
}

export function isBlockedUserId(userId: string): boolean {
  const id = normalizeUserId(userId);
  if (id === RASITECH_SUPER_ADMIN_USER_ID) return false;
  return memory.blockedUserIds.includes(id);
}

export async function addPaidUser(userId: string, actorId: string): Promise<boolean> {
  const id = normalizeUserId(userId);
  if (!id) return false;
  if (!memory.paidUserIds.includes(id)) {
    memory.paidUserIds.push(id);
  }
  memory.blockedUserIds = memory.blockedUserIds.filter((x) => x !== id);
  await appendActivity(actorId, 'admin_action', `Granted paid access to user ${id}`);
  return persist(actorId);
}

export async function verifyPaidUserOnCentral(userId: string): Promise<boolean> {
  if (!isCentralDbEnabled()) return false;
  invalidateAccessControlCache();
  const remote = await pullAccessControlFromCentral();
  if (!remote) return false;
  const id = normalizeUserId(userId);
  return remote.paidUserIds.map(normalizeUserId).includes(id);
}

export async function removePaidUser(userId: string, actorId: string) {
  const id = userId.trim();
  memory.paidUserIds = memory.paidUserIds.filter((x) => x !== id);
  await appendActivity(actorId, 'admin_action', `Revoked paid access from user ${id}`);
  await persist(actorId);
}

export async function blockUser(userId: string, actorId: string) {
  const id = userId.trim();
  if (!id || id === RASITECH_SUPER_ADMIN_USER_ID) return;
  if (!memory.blockedUserIds.includes(id)) {
    memory.blockedUserIds.push(id);
  }
  memory.paidUserIds = memory.paidUserIds.filter((x) => x !== id);
  await appendActivity(actorId, 'admin_action', `Blocked user ${id} from app`);
  await persist(actorId);
}

export async function unblockUser(userId: string, actorId: string) {
  const id = userId.trim();
  memory.blockedUserIds = memory.blockedUserIds.filter((x) => x !== id);
  await appendActivity(actorId, 'admin_action', `Unblocked user ${id}`);
  await persist(actorId);
}

export async function appendActivity(
  userId: string,
  type: RasiActivityType,
  message: string,
) {
  memory.activityLog.push({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    userId,
    type,
    message,
    timestamp: Date.now(),
  });
  if (memory.activityLog.length > MAX_LOG) {
    memory.activityLog = memory.activityLog.slice(-MAX_LOG);
  }
  const entry = memory.activityLog[memory.activityLog.length - 1];
  await persist(userId);
  if (entry) {
    void pushActivityToCentral(entry);
  }
}

export async function importAccessControl(json: string, actorId: string): Promise<boolean> {
  try {
    const parsed = normalize(JSON.parse(json) as RasiAccessControlData);
    memory = parsed;
    await appendActivity(actorId, 'admin_action', 'Imported access control configuration');
    await persist(actorId);
    return true;
  } catch {
    return false;
  }
}

export function exportAccessControl(): string {
  return JSON.stringify(memory, null, 2);
}
